/**
 * Product Variant Selection Storage
 * Saves and restores user's selected product variants (color, size, options) using localStorage
 *
 * Storage key format: theme:product-variant-{productId}
 * Data structure: { selectedOptions: [], variantId: number, timestamp: number }
 */

(function() {
  'use strict';

  const STORAGE_PREFIX = 'theme:product-variant-';

  function getPositionFromInput(input) {
    if (input.name?.startsWith('sw')) {
      const match = input.name.match(/^sw(\d+)-/);
      return match ? parseInt(match[1], 10) : null;
    }
    if (input.classList?.contains('pf-variant-select')) {
      const parent = input.closest('[data-unique-id]');
      return parent ? parseInt(parent.dataset.uniqueId, 10) : null;
    }
    return null;
  }

  function groupInputsByPosition(inputs) {
    const optionGroups = new Map();
    inputs.forEach(input => {
      const position = getPositionFromInput(input);
      if (position) {
        if (!optionGroups.has(position)) {
          optionGroups.set(position, []);
        }
        optionGroups.get(position).push(input);
      }
    });
    return optionGroups;
  }

  function initVariantStorage(form, productId) {
    if (!form || !productId) return;

    const storageKey = STORAGE_PREFIX + productId;
    let optionInputs = form.querySelectorAll('[name^="option-"]');
    const isPageFly = optionInputs.length === 0;

    if (isPageFly) {
      optionInputs = form.querySelectorAll('[name^="sw"], [data-option-name]');
    }

    let listenersActive = false;

    let variants = [];
    const variantsScript = form.querySelector('script[type="application/json"]');
    if (variantsScript) {
      try {
        variants = JSON.parse(variantsScript.textContent).variants || [];
      } catch (e) {
        console.warn('Failed to parse product variants:', e);
      }
    }

    if (!variants.length && window.productVariants) {
      variants = window.productVariants;
    }

    if (!variants.length && window.__pageflyProducts?.[productId]) {
      variants = window.__pageflyProducts[productId].variants || [];
    }

    function saveVariantSelection(selectedOptions, matchedVariant) {
      if (!matchedVariant) return;

      try {
        localStorage.setItem(storageKey, JSON.stringify({
          selectedOptions,
          variantId: matchedVariant.id,
          timestamp: Date.now()
        }));
      } catch (e) {
        console.warn('Failed to save variant selection:', e);
      }
    }

    function restoreVariantSelection() {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const urlVariantId = urlParams.get('variant');

        if (isPageFly && urlVariantId) {
          const matchedVariant = variants.find(v => v.id == urlVariantId);
          if (matchedVariant) {
            localStorage.setItem(storageKey, JSON.stringify({
              selectedOptions: matchedVariant.options,
              variantId: matchedVariant.id,
              timestamp: Date.now()
            }));
          }
          return;
        }

        const savedData = localStorage.getItem(storageKey);
        if (!savedData) return;

        const { selectedOptions: savedOptions, variantId } = JSON.parse(savedData);
        if (!savedOptions?.length) return;

        const matchedVariant = variants.find(v => v.id === variantId);
        if (!matchedVariant) {
          localStorage.removeItem(storageKey);
          return;
        }

        if (isPageFly) {
          restorePageFlySelection(savedOptions);
        } else {
          restoreStandardSelection(savedOptions);
        }

      } catch (e) {
        console.warn('Failed to restore variant selection:', e);
        localStorage.removeItem(storageKey);
      }
    }

    function restoreStandardSelection(savedOptions) {
      function restoreInContainer(container) {
        if (!container) return;

        const inputsToClick = [];

        for (let index = 0; index < savedOptions.length; index++) {
          const optionValue = savedOptions[index];
          const optionInput = container.querySelector(`[name="option-${index}"]`);

          if (!optionInput) continue;

          if (optionInput.type === 'radio') {
            const radioToSelect = container.querySelector(`input[name="option-${index}"][value="${CSS.escape(optionValue)}"]`);
            if (!radioToSelect) {
              localStorage.removeItem(storageKey);
              return;
            }
            inputsToClick.push(radioToSelect);
          } else if (optionInput.tagName === 'SELECT') {
            if (!Array.from(optionInput.options).some(opt => opt.value === optionValue)) {
              localStorage.removeItem(storageKey);
              return;
            }
            optionInput.value = optionValue;
            inputsToClick.push(optionInput);
          }
        }

        inputsToClick.forEach(input => {
          if (input.type === 'radio') {
            if (!input.checked) {
              input.click();
            } else {
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          } else if (input.tagName === 'SELECT') {
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      const mobileContainer = form.querySelector('.pi_mobile') || document.querySelector('.pi_mobile');
      const desktopContainer = form.querySelector('.pi_desktop') || document.querySelector('.pi_desktop');

      if (mobileContainer || desktopContainer) {
        if (desktopContainer) restoreInContainer(desktopContainer);
        if (mobileContainer) restoreInContainer(mobileContainer);

        for (let index = 0; index < savedOptions.length; index++) {
          const optionValue = savedOptions[index];
          const selectInput = form.querySelector(`select[name="option-${index}"]`);
          if (selectInput && !selectInput.closest('.pi_mobile') && !selectInput.closest('.pi_desktop')) {
            if (Array.from(selectInput.options).some(opt => opt.value === optionValue)) {
              selectInput.value = optionValue;
              selectInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        }
      } else {
        restoreInContainer(form);
      }
    }

    function restorePageFlySelection(savedOptions) {
      const optionGroups = groupInputsByPosition(optionInputs);
      const sortedPositions = Array.from(optionGroups.keys()).sort((a, b) => a - b);

      sortedPositions.forEach((position, index) => {
        const inputs = optionGroups.get(position);
        const targetValue = savedOptions[index];

        if (!targetValue) return;

        for (const input of inputs) {
          if (input.type === 'radio' && input.value === targetValue) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
            input.dispatchEvent(new Event('click', { bubbles: true }));
            break;
          } else if (input.tagName === 'SELECT') {
            input.value = targetValue;
            input.dispatchEvent(new Event('change', { bubbles: true }));
            break;
          }
        }
      });
    }

    function setupChangeListeners() {
      optionInputs.forEach(input => {
        input.addEventListener('change', () => {
          if (!listenersActive) return;

          const selectedOptions = isPageFly
            ? collectPageFlyOptions()
            : collectStandardOptions();

          const matchedVariant = variants.find(v =>
            JSON.stringify(v.options) === JSON.stringify(selectedOptions)
          ) || variants.find(v =>
            v.options.some((opt, idx) => opt === selectedOptions[idx])
          );

          if (matchedVariant) {
            saveVariantSelection(selectedOptions, matchedVariant);
          }
        });
      });
    }

    function collectStandardOptions() {
      return Array.from(optionInputs)
        .map(el => el.type === 'radio' ? (el.checked ? el.value : null) : el.value)
        .filter(Boolean);
    }

    function collectPageFlyOptions() {
      const optionGroups = groupInputsByPosition(optionInputs);
      const selectedOptions = [];
      const sortedPositions = Array.from(optionGroups.keys()).sort((a, b) => a - b);

      sortedPositions.forEach(position => {
        const inputs = optionGroups.get(position);
        for (const input of inputs) {
          if ((input.type === 'radio' && input.checked) || (input.tagName === 'SELECT' && input.value)) {
            selectedOptions.push(input.value);
            break;
          }
        }
      });

      return selectedOptions;
    }

    if (variants.length && optionInputs.length) {
      setupChangeListeners();

      window.addEventListener('load', () => {
        setTimeout(() => {
          try {
            restoreVariantSelection();
          } catch (error) {
            console.error('[Product Storage] Error during restoration:', error);
          } finally {
            listenersActive = true;
          }
        }, 300);
      }, { once: true });
    }
  }

  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return;
    }

    const productForms = document.querySelectorAll(
      'form[id*="product-form"], ' +
      'form#custom-product-form, ' +
      'form[action*="/cart/add"], ' +
      'form.product-form, ' +
      'form[data-product-form], ' +
      'form.shopify-product-form'
    );

    productForms.forEach(form => {
      let productId = form.dataset.productId ||
                      form.dataset.product ||
                      form.dataset.productid ||
                      form.getAttribute('data-product-id');

      if (!productId && window.productId) {
        productId = window.productId;
      }

      if (!productId) {
        const variantsScript = form.querySelector('script[type="application/json"]');
        if (variantsScript) {
          try {
            const productData = JSON.parse(variantsScript.textContent);
            productId = productData.id || productData.product?.id;
          } catch (e) {
            console.warn('Failed to parse product ID:', e);
          }
        }
      }

      if (!productId) {
        const hiddenInput = form.querySelector('input[name="id"]');
        if (hiddenInput?.value && window.productVariants) {
          const variant = window.productVariants.find(v => v.id == hiddenInput.value);
          productId = variant?.product_id;
        }
      }

      if (productId) {
        initVariantStorage(form, productId);
      }
    });
  }

  init();

})();
